import { NAV_SECTIONS, navKindOf, navSectionOf } from "@tc/sdk";
/**
 * Most tabs a section shows in its row (Jamie, 17/10/26): five, plus the section's Settings cog, so a 375px phone
 * fits them all with no sideways scroll and nothing hidden. There is no "More": past five, the extra tabs' pages join
 * the fifth tab as its small links (sectionTabs).
 */
export const MAX_TABS = 5;
/**
 * THE PLATFORM'S DEFAULT TABS (Jamie, 14/10/26): each section condensed to at most six, until modules set `tab`
 * themselves. Path → [tab, icon].
 */
const T = (tab, icon, ...paths) => paths.map((p) => [p, [tab, icon]]);
const DEFAULT_TABS = new Map([
    ...T("Overview", "fuel", "/fuel"), ...T("Prices", "tag", "/fuel/pricing", "/fuel/checks"), ...T("Margins", "trending-up", "/fuel/margin"),
    ...T("Tanks", "droplet", "/wetstock", "/wetstock/deliveries", "/wetstock/ordering", "/wetstock/invoices"),
    ...T("Car wash", "car", "/carwash", "/carwash/machines", "/carwash/imports"),
    ...T("Products", "tag", "/products", "/products/scan", "/products/issues", "/products/new"), ...T("Pricing", "pound", "/prices", "/prices/changes", "/product-updates"),
    ...T("Promotions", "sparkle", "/promotions", "/promotions/plan", "/promotions/ends"),
    ...T("Ordering", "basket", "/wholesaler/order", "/wholesaler/invoices", "/wholesaler/rebate"),
    ...T("Suppliers", "truck", "/suppliers", "/suppliers/approved", "/suppliers/notices", "/suppliers/files", "/suppliers/rebates"),
    ...T("Sign-off", "check-circle", "/cash-office", "/cash-office/crs", "/cash-office/srs"), ...T("Charges", "flag", "/charges"),
    ...T("Rent", "receipt", "/rent-review", "/rent-review/mine"), ...T("Commission", "percent", "/commissions"), ...T("Operators", "users", "/operators", "/operators/fees"),
    ...T("Tasks", "tasks", "/tasks", "/tasks/checks", "/projects"), ...T("Audits", "check", "/audits"), ...T("Maintenance", "wrench", "/maintenance", "/maintenance/assets"),
    ...T("Compliance", "shield", "/compliance"), ...T("Screens", "broadcast", "/signage", "/signage/adverts"), ...T("Sites", "pin", "/sites", "/sites/ratings"),
    ...T("Me", "user", "/hr/me", "/learning"), ...T("Chat", "message", "/chat", "/chat/oversee"), ...T("Staff", "users", "/hr", "/learning/team"),
    ...T("Rota", "calendar", "/hr/rota", "/hr/leave", "/hr/time"), ...T("Expenses", "receipt", "/hr/expenses"),
    ...T("Setup", "check", "/settings/start", "/settings/setup"), ...T("Logins", "users", "/settings/people", "/settings/security", "/settings/profile"), ...T("Your plan", "card", "/settings/plan", "/settings/invoice"), ...T("Branding", "palette", "/settings/branding", "/settings/emails"),
    ...T("Data", "broadcast", "/settings/figures", "/settings/connections", "/settings/credentials", "/settings/messages", "/reports/settings", "/reports/manage"),
]);
const ADMIN_PERMS = new Set(["settings", "manage", "build", "oversee", "perform"]);
export function canFor(permissions) {
    const p = new Set(permissions);
    return (perm) => p.has("*") || p.has(perm) || p.has(`${perm.split(".")[0]}.*`);
}
/** Every page in the menu this person can open, each with its one section. */
export function buildNav(manifests, session) {
    const can = canFor(session.permissions);
    const models = Object.values(session.siteModels ?? {});
    const when = (c) => (!c?.operatingModel || models.some((m) => m !== null && c.operatingModel.includes(m)))
        && (!c?.fuel || !session.fuelSites || session.fuelSites.length > 0);
    const items = [];
    for (const m of manifests) {
        if (!session.modules.includes(m.id) || !when(m.appliesTo))
            continue;
        for (const n of m.nav) {
            if (!when(n.when) || !can(`${m.id}.${n.permission}`))
                continue;
            // Modules sold on their own (26/10/26): a page that needs another module, or this module's switched-off feature.
            if (n.requires?.length && !n.requires.some((r) => session.modules.includes(r)))
                continue;
            if (n.feature && session.features?.[`${m.id}.${n.feature}`] === false)
                continue;
            if (n.oldCloud && !session.oldCloud)
                continue;
            // A page offered under two permissions (e.g. Compliance → Upload & file: manage or submit) shows once.
            if (items.some((x) => x.path === n.path))
                continue;
            const kind = navKindOf(n);
            const def = DEFAULT_TABS.get(n.path);
            items.push({ label: n.label, path: n.path, icon: n.icon ?? "", order: n.order ?? 50, section: navSectionOf(m.id, n), kind,
                admin: kind === "page" && (n.admin ?? ADMIN_PERMS.has(n.permission)), blurb: n.blurb,
                tab: n.tab ?? def?.[0] ?? n.label, tabIcon: n.tabIcon ?? (n.tab ? n.icon : def?.[1]) ?? n.icon ?? "" });
        }
    }
    const company = (label, icon, path, order, perm, blurb) => {
        if (can(perm))
            items.push({ label, icon, path, order, section: "settings", kind: "settings", admin: false, company: true, blurb, tab: DEFAULT_TABS.get(path)?.[0] ?? label, tabIcon: DEFAULT_TABS.get(path)?.[1] ?? icon });
    };
    // Head office imports (the Nozl Import Bridge): under Products (Jamie 28/10/26), only for a company that switched it
    // on in Setup (Products → Head office imports; off by default: a company without a head office never sees it).
    if (can("company.figures") && session.modules.includes("products") && session.features?.["products.headOfficeImports"] === true
        && !items.some((x) => x.path === "/head-office/imports")) {
        // Called the Nozl Bridge (Jamie 29/10/26): every file going to the head office PC, sent by a module or uploaded by hand.
        // Its own menu section at the foot of the menu (29/10/26), not a Shop sub-page: it's for the extra uploads.
        items.push({ label: "Nozl Bridge", icon: "upload", path: "/head-office/imports", order: 12, section: "bridge", kind: "page", admin: false,
            blurb: "Every file to the head office back office (CBE): sent by Pricing, Product updates, Promotions and Products, or uploaded by hand",
            tab: "Nozl Bridge", tabIcon: "upload" });
    }
    company("Set-up", "check", "/settings/start", 899, "company.setup", "Plain questions, step by step: your answers set up every part of Nozl you have");
    company("Checklist", "check", "/settings/setup", 900, "company.setup", "The logins and data feeds that connect your sites and systems");
    company("Logins & roles", "users", "/settings/people", 905, "company.people", "Who can sign in, their level and sites");
    company("Branding", "palette", "/settings/branding", 910, "company.branding", "Your logo and colours");
    company("Emails & texts", "message", "/settings/emails", 912, "company.branding", "Your emails' footer and the wording of each message");
    company("Figures", "chart", "/settings/figures", 915, "company.figures", "Daily figures and data feeds");
    company("Your plan", "card", "/settings/plan", 907, "company.billing", "Packs, charges and Direct Debit");
    company("Sign-in security", "check", "/settings/security", 925, "company.setup", "Two-factor, PIN and idle lock rules");
    company("Connections", "broadcast", "/settings/connections", 930, "company.setup", "Every outside system, and whether it's working");
    company("Credentials", "settings", "/settings/credentials", 935, "company.setup", "Logins Nozl uses for your suppliers and systems");
    company("Message log", "message", "/settings/messages", 940, "company.setup", "Every push, WhatsApp and text sent");
    items.push({ label: "My settings", icon: "user", path: "/settings/profile", order: 990, section: "settings", kind: "page", admin: false, blurb: "Your password, sign-in, alerts and phone", tab: "Logins", tabIcon: "users" });
    return items.sort((a, b) => a.order - b.order);
}
/** The sections this person sees, in order, each with its pages. */
export function menuSections(nav) {
    return NAV_SECTIONS.map((s) => {
        const items = nav.filter((n) => n.section === s.id);
        // A section opens on its first tab's page (Fuel → Overview); its page of every page is /s/<id>.
        const first = sectionTabs({ id: s.id, items })[0];
        const home = s.id === "dashboard" ? "/" : first?.path ?? items[0]?.path ?? `/section/${s.id}`;
        return { ...s, items, home };
    }).filter((s) => s.items.length > 0);
}
/**
 * A section's tab row: its everyday pages grouped by `tab`, in menu order. Settings pages aren't tabs (they're
 * behind the row's Settings link) except in the Settings section itself, where everything is.
 */
export function sectionTabs(section) {
    const tabs = [];
    for (const n of section.items) {
        if (n.kind === "settings" && section.id !== "settings")
            continue;
        const t = tabs.find((x) => x.label === n.tab);
        if (t)
            t.items.push(n);
        else
            tabs.push({ label: n.tab, icon: n.tabIcon, path: n.path, items: [n] });
    }
    // Never more than MAX_TABS: any extra tabs' pages join the last one (no "More", Jamie 17/10/26).
    if (tabs.length > MAX_TABS) {
        const last = tabs[MAX_TABS - 1];
        for (const x of tabs.splice(MAX_TABS))
            last.items.push(...x.items);
    }
    // A tab leads to its main page: the shortest address in it (Cash office → /cash-office, not /cash-office/crs).
    // A section's reports sharing a tab (Cash Office's under Sign-off, 04/11/26) never lead it while it has pages of its own.
    for (const t of tabs) {
        const own = t.items.filter((n) => !n.path.startsWith("/reports/"));
        t.path = [...(own.length ? own : t.items)].sort((a, b) => a.path.length - b.path.length)[0].path;
    }
    return tabs;
}
/** The tab a path is under (the one holding the longest matching page). */
export function tabFor(tabs, path) {
    const entry = entryFor(tabs.flatMap((t) => t.items), path);
    return entry && tabs.find((t) => t.items.includes(entry));
}
/** A section's pages in the order its home page and header show them. */
export function sectionGroups(items) {
    return [
        { id: "daily", label: "Daily", items: items.filter((n) => n.kind === "page" && !n.admin) },
        { id: "manage", label: "Manage", items: items.filter((n) => n.kind === "page" && n.admin) },
        { id: "settings", label: "Settings", items: items.filter((n) => n.kind === "settings") },
    ].filter((g) => g.items.length > 0);
}
/** The menu entry a path belongs to: the longest matching entry (Assets inside Maintenance, etc.). */
export function entryFor(nav, path) {
    return nav.filter((n) => (n.path === "/" ? path === "/" : path === n.path || path.startsWith(`${n.path}/`))).sort((a, b) => b.path.length - a.path.length)[0];
}
/** The section a path is in: a section home (/s/fuel), a page's section, or none (More, a public page). */
export function sectionFor(nav, path) {
    const m = /^\/s\/([a-z]+)$/.exec(path);
    if (m)
        return (NAV_SECTIONS.find((s) => s.id === m[1])?.id) ?? null;
    return entryFor(nav, path)?.section ?? null;
}
/**
 * WHERE YOU WERE IN EACH SECTION AND TAB (Jamie 29/10/26: "when flicking through sub menus … always remember which one
 * you were on in each category even if you leave and come back"). The last page opened under each tab, and in each
 * section, kept in this browser; a section's menu link and a tab go back there (if you can still open it), else to
 * their own first page. The dashboard is always Home.
 */
const LAST = "nozl.lastPages";
function readLast() { try {
    const v = JSON.parse(localStorage.getItem(LAST) ?? "{}");
    return { s: v.s ?? {}, t: v.t ?? {} };
}
catch {
    return { s: {}, t: {} };
} }
export function rememberPage(section, path) {
    if (section.id === "dashboard" || !section.items.some((n) => n.path === path))
        return;
    const tab = tabFor(sectionTabs(section), path);
    const l = readLast();
    l.s[section.id] = path;
    if (tab)
        l.t[`${section.id}|${tab.label}`] = path;
    try {
        localStorage.setItem(LAST, JSON.stringify(l));
    }
    catch { /* private window: just not remembered */ }
}
/** Where a section's menu link goes: the last page you had open in it, or its first. */
export function resumeSection(section) {
    if (section.id === "dashboard")
        return section.home;
    const p = readLast().s[section.id];
    return p && section.items.some((n) => n.path === p) ? p : section.home;
}
/** Where a tab goes: the last page you had open under it, or its first. */
export function resumeTab(sectionId, tab) {
    const p = readLast().t[`${sectionId}|${tab.label}`];
    return p && tab.items.some((n) => n.path === p) ? p : tab.path;
}
